package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.FormField
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.IsbnLookupResult
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun IsbnLookupForm(
    apiClient: ApiClient,
    onCancel: (() -> Unit)? = null
) {
    val colors = AppTheme.colors
    var isbn by remember { mutableStateOf("") }
    var isSearching by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var result by remember { mutableStateOf<IsbnLookupResult?>(null) }

    val scope = rememberCoroutineScope()

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            themeTransition()
        }
    }) {
        H2({
            style {
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.lg)
            }
        }) { Text("ISBN Lookup") }

        P({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
                marginBottom(Spacing.xxl)
            }
        }) {
            Text("Search for audiobook metadata by ISBN using Google Books and Open Library.")
        }

        // ISBN input
        FormField("ISBN (10 or 13 digits)") {
            Input(InputType.Text) {
                value(isbn)
                onInput { event -> isbn = event.value.trim() }
                attr("placeholder", "e.g. 9780140449136")
                if (isSearching) disabled()
                style {
                    width(100.percent)
                    padding(Spacing.sm, Spacing.md)
                    fontSize(Typography.base)
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    backgroundColor(Color(colors.surface))
                    color(Color(colors.text))
                    themeTransition()
                    property("box-sizing", "border-box")
                }
            }
        }

        if (isSearching) {
            IndeterminateProgressBar("Looking up ISBN...")
        }

        errorMessage?.let { error ->
            ErrorBanner(error)
        }

        // Result display
        result?.let { lookupResult ->
            if (lookupResult.found) {
                IsbnResultView(lookupResult)
            } else {
                Div({
                    style {
                        backgroundColor(Color(colors.surfaceHover))
                        padding(Spacing.lg)
                        borderRadius(BorderRadius.md)
                        marginBottom(Spacing.lg)
                        textAlign("center")
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.textMuted))
                        }
                    }) { Text("No results found for this ISBN.") }
                }
            }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexEnd)
                gap(Spacing.md)
                marginTop(Spacing.xxl)
            }
        }) {
            if (onCancel != null) {
                Button({
                    if (isSearching) disabled()
                    onClick { if (!isSearching) onCancel() }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color(colors.text))
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                    }
                }) { Text("Close") }
            }

            Button({
                val isValid = isbn.length in 10..13 && isbn.all { it.isDigit() || it == 'X' || it == 'x' }
                if (!isValid || isSearching) disabled()
                onClick {
                    isSearching = true
                    errorMessage = null
                    result = null
                    scope.launch {
                        try {
                            result = apiClient.isbnLookup(isbn)
                        } catch (e: Exception) {
                            errorMessage = e.message ?: "Lookup failed"
                        } finally {
                            isSearching = false
                        }
                    }
                }
                style {
                    padding(Spacing.sm, Spacing.lg)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color("#ffffff"))
                    backgroundColor(
                        if (isbn.length < 10 || isSearching) Color(colors.textMuted)
                        else Color(colors.primary)
                    )
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor(if (isbn.length < 10 || isSearching) "not-allowed" else "pointer")
                }
            }) { Text(if (isSearching) "Searching..." else "Lookup") }
        }
    }

    Style {
        """
        @keyframes indeterminate {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
        }
        """.trimIndent()
    }
}

@Composable
private fun IsbnResultView(result: IsbnLookupResult) {
    val colors = AppTheme.colors

    Div({
        style {
            backgroundColor(Color(colors.surfaceHover))
            borderRadius(BorderRadius.md)
            padding(Spacing.lg)
            marginBottom(Spacing.lg)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.lg)
            }
        }) {
            // Cover image
            result.coverImageUrl?.let { url ->
                Img(url) {
                    style {
                        width(80.px)
                        height(120.px)
                        borderRadius(BorderRadius.md)
                        property("object-fit", "cover")
                        flexShrink(0)
                    }
                }
            }

            // Details
            Div({ style { flex("1") } }) {
                result.title?.let { title ->
                    Div({
                        style {
                            fontWeight("600")
                            fontSize(Typography.lg)
                            color(Color(colors.text))
                            marginBottom(Spacing.sm)
                        }
                    }) { Text(title) }
                }

                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.textMuted))
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(Spacing.xs)
                    }
                }) {
                    Div { Span({ style { fontWeight("500") } }) { Text("ISBN: ") }; Text(result.isbn13) }
                    result.author?.let { Div { Span({ style { fontWeight("500") } }) { Text("Author: ") }; Text(it) } }
                    result.publisher?.let { Div { Span({ style { fontWeight("500") } }) { Text("Publisher: ") }; Text(it) } }
                    result.publicationDate?.let { Div { Span({ style { fontWeight("500") } }) { Text("Published: ") }; Text(it) } }
                    result.language?.let { Div { Span({ style { fontWeight("500") } }) { Text("Language: ") }; Text(it) } }
                    if (result.categories.isNotEmpty()) {
                        Div { Span({ style { fontWeight("500") } }) { Text("Categories: ") }; Text(result.categories.joinToString(", ")) }
                    }
                }

                result.description?.let { desc ->
                    Div({
                        style {
                            fontSize(13.px)
                            color(Color(colors.textMuted))
                            marginTop(Spacing.md)
                            maxHeight(100.px)
                            overflowY("auto")
                            property("line-height", "1.5")
                        }
                    }) { Text(desc) }
                }
            }
        }
    }
}
